import React, { useRef, useContext } from 'react';
import {
  Alert, Button, Grid, TextField, MenuItem, Snackbar, useTheme, Skeleton, Typography, InputAdornment,
  SvgIcon, Autocomplete,
} from '@mui/material';
import { SearchSm, Download01, ArrowUp, Plus } from '../../../assets';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import { Section } from '../../../components/ui/Section';
import BulkOrderTable from './components/BulkOrderTable';
import BulkOrderDateCalendar from './components/BulkOrderDateCalendar';
import DemandModifierModal from '../DemandModifiers/components/DemandModifierModal';
import { QuantityWarningDialog } from '../../../components/shared/orders/QuantityWarningDialog';
import useLogError from '../../../hooks/useLogError';
import { useBulkOrderFilters } from './hooks/useBulkOrderFilters';
import { useBulkOrderData } from './hooks/useBulkOrderData';
import { usePredictedDemands } from './hooks/usePredictedDemands';

const BulkOrderManagementPage = () => {
  const { t } = useTranslation(['bulkOrderManagement', 'orderForm']);
  const theme = useTheme();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { logError } = useLogError();

  const canReadBulkOrders = hasPermissionTo(['BulkOrdersRead']);
  const canWriteBulkOrders = hasPermissionTo(['BulkOrdersWrite']);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { itemSearch, setItemSearch, category, setCategory, appliedItemSearch, appliedCategory,
    handleSearch, clearFilters } = useBulkOrderFilters();

  const {
    orderState: {
      loading, selectedMarket, orderType, supplierNumber, selectedDate,
      orderTypes, suppliers, markets, bulkOrderItems, selectedStores,
      odsAvailability, isDsdOrderType, bulkOrdersLoadVersion,
      categoryOptions, getCategoryOptionLabel, canSubmit, showNoDeliveryDatesMessage,
    },
    filterHandlers: { handleMarketChange, handleOrderTypeChange, handleSupplierChange, handleDateChange },
    selectionHandlers: { handleStoreGroupSelection },
    dataHandlers: { handleOverrideChange, handleDownload, handleFileUpload, handleSubmit },
    quantityWarning: { showQuantityWarningDialog, setShowQuantityWarningDialog, warningMessage },
    notification: { snackbar, handleSnackbarOpen, handleSnackbarClose },
    demandModifier: {
      isDemandModifierModalOpen, editingModifier,
      skuError, setSkuError,
      handleOpenDemandModifierModal, handleCloseDemandModifierModal,
      handleSaveDemandModifier, handlePromoClick,
    },
  } = useBulkOrderData({
    selectedStore,
    logError,
    t,
    appliedItemSearch,
    appliedCategory,
    clearFilters,
    hasPermissionTo,
  });

  const { predictedDemands } = usePredictedDemands({
    selectedStore,
    bulkOrderItems,
    selectedDate,
    bulkOrdersLoadVersion,
    logError,
  });

  const selectedCategory = categoryOptions.find(cat => cat.id === category) ?? categoryOptions[0];
  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      handleSnackbarOpen(t('noFileSelected'), 'warning');
      return;
    }
    await handleFileUpload(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!canReadBulkOrders) {
    return null;
  }

  return (
    <PageArea>
      <Grid item
        mb={3}>
        <PageTitle>{t('title')}</PageTitle>
      </Grid>

      <Grid container
        sx={{ marginTop: '6px' }}
        spacing={4}>
        <Grid item
          xs={12}>
          <Section>
            <Grid container
              xs={12}
              sx={{ pt: '20px' }}>
              <Grid item
                container
                alignItems={'center'}
                xs={12}>
                <Grid item
                  xs={12}
                  md={3}
                  container
                  sx={{ p: 3, pl: 5, pb: 5 }}>
                  <Grid item
                    xs={12}
                    sx={{ mb: 4 }}>
                    <Typography variant="textMD"
                      fontWeight={500}
                      sx={{ color: theme.palette.custom.gray[700] }}>
                      {t('selectOrder')}
                    </Typography>
                  </Grid>
                  <Grid item
                    xs={12}>
                    <BulkOrderDateCalendar
                      value={selectedDate}
                      onChange={handleDateChange}
                      disabled={loading || !selectedMarket}
                      disableHighlightToday={true}
                      odsAvailability={odsAvailability}
                    />
                    {showNoDeliveryDatesMessage && (
                      <Typography
                        variant="textSM"
                        sx={{ mt: 2, color: theme.palette.custom.gray[700] }}
                      >
                        {t('noDeliveryDatesFound')}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                <Grid item
                  container
                  alignItems={'center'}
                  xs={12}
                  md={9}
                  sx={{ p: 3, pr: 5, pb: 5 }}>
                  <Grid item
                    container
                    spacing={4}>
                    <Grid item
                      xs={12}
                      md={isDsdOrderType ? 4 : 6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('orderType')}
                        disabled={loading || !selectedMarket || orderTypes.length === 0}
                        value={orderType}
                        onChange={(e) => handleOrderTypeChange(Number(e.target.value))}
                        required
                        select>
                        {orderTypes.map((type) => (
                          <MenuItem key={type.id}
                            value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {isDsdOrderType && (
                      <Grid item
                        xs={12}
                        md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('orderForm:supplier', { defaultValue: 'Supplier' })}
                          disabled={loading || !selectedMarket || suppliers.length === 0}
                          value={supplierNumber}
                          onChange={(e) => handleSupplierChange(e.target.value)}
                          required
                          select>
                          {suppliers.map((supplier) => (
                            <MenuItem key={supplier.supplierId}
                              value={supplier.supplierNumber}>
                              {supplier.supplierName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}

                    <Grid item
                      xs={12}
                      md={isDsdOrderType ? 4 : 6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('franchise')}
                        disabled={loading}
                        value={selectedMarket}
                        onChange={(e) => handleMarketChange(e.target.value)}
                        required
                        select>
                        <MenuItem value="">
                          <em>{t('selectFranchise')}</em>
                        </MenuItem>
                        {markets.map((market) => (
                          <MenuItem key={market.customerTypeId}
                            value={market.customerTypeId.toString()}>
                            {market.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item
                      xs={12}
                      md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('searchBy')}
                        placeholder={t('searchBy')}
                        value={itemSearch}
                        onChange={(e) => setItemSearch(e.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            handleSearch();
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SvgIcon>
                                <SearchSm />
                              </SvgIcon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item
                      xs={12}
                      md={6}>
                      <Autocomplete
                        size="small"
                        options={categoryOptions}
                        getOptionLabel={getCategoryOptionLabel}
                        value={selectedCategory}
                        onChange={(event, newValue) => {
                          setCategory(newValue ? newValue.id : -1);
                        }}
                        renderInput={(params) => (
                          <TextField {...params}
                            label={t('category')}
                            placeholder={t('choose')} />
                        )}
                      />
                    </Grid>

                    <Grid item
                      xs={12}
                      md={6}>
                      <Button
                        fullWidth
                        variant="secondary"
                        disabled={loading}
                        size="lg"
                        onClick={clearFilters}>
                        {t('clearFilters')}
                      </Button>
                    </Grid>

                    <Grid item
                      xs={12}
                      md={6}>
                      <Button
                        fullWidth
                        variant="primary"
                        disabled={loading || !selectedMarket || orderType === -1 || !selectedDate}
                        size="lg"
                        startIcon={<SearchSm />}
                        onClick={handleSearch}>
                        {t('search')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                item
                container
                alignItems={'center'}
                xs={12}
                spacing={3}
                sx={{ paddingX: 5, paddingTop: 4, paddingBottom: 3 }}
              >
                <Grid item
                  xs={12}
                  md={3}>
                  <Button
                    fullWidth
                    variant="secondary"
                    disabled={loading}
                    startIcon={<SvgIcon><Download01 /></SvgIcon>}
                    size="lg"
                    onClick={handleDownload}>
                    {t('download')}
                  </Button>
                </Grid>

                {canWriteBulkOrders && (
                  <Grid item
                    xs={12}
                    md={3}>
                    <Button
                      fullWidth
                      variant="secondary"
                      disabled={loading}
                      startIcon={<SvgIcon><ArrowUp /></SvgIcon>}
                      size="lg"
                      onClick={handleUploadButtonClick}>
                      {t('upload')}
                    </Button>
                    <input
                      type="file"
                      style={{ display: 'none' }}
                      aria-label={t('upload')}
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".xlsx, .xls, .csv"
                    />
                  </Grid>
                )}

                {canWriteBulkOrders && (
                  <Grid item
                    xs={12}
                    md={3}>
                    <Button
                      fullWidth
                      variant="primary"
                      disabled={loading}
                      startIcon={<SvgIcon><Plus /></SvgIcon>}
                      size="lg"
                      onClick={() => handleOpenDemandModifierModal()}>
                      {t('createDemandModifier')}
                    </Button>
                  </Grid>
                )}

                {canWriteBulkOrders && (
                  <Grid item
                    xs={12}
                    md={3}>
                    <Button
                      fullWidth
                      variant="primary"
                      disabled={!canSubmit()}
                      size="lg"
                      onClick={handleSubmit}>
                      {t('submitOrders')}
                    </Button>
                  </Grid>
                )}
              </Grid>

              <Grid item
                xs={12}
                sx={{ mt: 4 }}>
                {loading ? (
                  <Skeleton variant="rectangular"
                    width={'100%'}
                    height={'max(95vh, 400px)'} />
                ) : (
                  <BulkOrderTable
                    items={bulkOrderItems}
                    onOverrideChange={handleOverrideChange}
                    selectedStores={selectedStores}
                    onStoreGroupSelection={handleStoreGroupSelection}
                    onPromoClick={handlePromoClick}
                    predictedDemands={predictedDemands}
                    deliveryDate={selectedDate?.format('YYYY-MM-DD')}
                  />
                )}
              </Grid>
            </Grid>
          </Section>
        </Grid>
      </Grid>

      <DemandModifierModal
        open={isDemandModifierModalOpen}
        onClose={handleCloseDemandModifierModal}
        onSave={handleSaveDemandModifier}
        modifier={editingModifier}
        categories={categoryOptions}
        skuError={skuError}
        onClearSkuError={() => setSkuError('')}
      />

      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={snackbar.message.includes('autosav') ? null : 7000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <QuantityWarningDialog
        setShowQuantityWarningDialog={setShowQuantityWarningDialog}
        message={warningMessage}
        showDialog={showQuantityWarningDialog}
      />
    </PageArea>
  );
};

export default BulkOrderManagementPage;
